import { Decimal, type Numeric } from "decimal.js-light";

// decimal.js-light throws "[DecimalError] Invalid argument: <value>" when its constructor
// is given NaN, undefined, or null. Callers reach these utilities with possibly-undefined
// values (e.g. calculateBuilderCost(unitCost) where unitCost comes from a partial API
// response). Coerce to 0 so the arithmetic result stays numeric and downstream formatters
// can render it instead of crashing the surrounding component tree.
const toDecimal = (value: Numeric | null | undefined): Decimal => {
  if (value === null || value === undefined) {
    return new Decimal(0);
  }
  if (typeof value === "number" && Number.isNaN(value)) {
    return new Decimal(0);
  }
  return new Decimal(value);
};

/**
 * Performs addition while avoiding issues arising from floating point error.
 * To get the raw value, simply call `.toNumber()` on the result
 * @param a
 * @param b
 *
 * @example
 * add(0.3, 0.2) => 0.5
 */
export const add = (a: Numeric, b: Numeric) => {
  return toDecimal(a).add(toDecimal(b));
};

/**
 * Performs subtraction while avoiding issues arising from floating point error.
 * To get the raw value, simply call `.toNumber()` on the result
 * @param a
 * @param b
 *
 * @example
 * subtract(0.3, 0.2) => 0.1
 */
export const subtract = (a: Numeric, b: Numeric) => {
  return toDecimal(a).sub(toDecimal(b));
};

/**
 * Performs multiplication while avoiding issues arising from floating point error
 * To get the raw value, simply call `.toNumber()` on the result
 * @param a
 * @param b
 *
 * @example
 * multiply(0.3, 0.2) => 0.06
 */
export const multiply = (a: Numeric, b: Numeric) => {
  return toDecimal(a).mul(toDecimal(b));
};

/**
 * Performs division while avoiding issues arising from floating point error
 * To get the raw value, simply call `.toNumber()` on the result
 * @param a dividend
 * @param b divisor
 *
 * @example
 * divide(0.3, 0.2) => 1.5
 */
export const divide = (a: Numeric, b: Numeric) => {
  return toDecimal(a).div(toDecimal(b));
};

/**
 * Returns a supplied numeric expression rounded to the nearest number of decimals.
 * Helps avoid floating point rounding errors if the number of decimals supplied are < the precision error location.
 *
 * @param value Value to round
 * @param decimals Number of decimals to round to
 *
 * @example
 * round(0.1 + 0.2,  1) => 0.3
 * round(0.1 + 0.2, 10) => 0.3
 * round(0.1 + 0.2, 17) => 0.30000000000000004
 * See unit tests for more examples
 */
export const round = (value: Numeric, decimals: number = 0) => {
  // Preserve NaN so NumericFormat can render it as empty instead of showing 0.
  if (typeof value === "number" && Number.isNaN(value)) {
    return NaN;
  }
  return toDecimal(value).toDecimalPlaces(decimals).toNumber();
};

/**
 * Returns a number rounded to the number of decimals specified using round-to-even rounding.
 * When a number is exactly halfway between two others it is rounded toward the nearest even number, similar to MidpointRounding.ToEven in C#.
 * Also known as banker's rounding, this approach is often used in financial calculations to reduce bias in rounding.
 *
 * @param value Value to round
 * @param decimals Number of decimals to round to
 *
 * @example
 * roundToEven(1.495, 2) => 1.50
 * roundToEven(1.505, 2) => 1.50
 * roundToEven(1.515, 2) => 1.52
 */
export function roundToEven(value: number, decimals: number = 2) {
  const multiplier = Math.pow(10, decimals);
  const adjustedValue = value * multiplier;
  const roundedValue = Math.round(adjustedValue);
  const fractionalPart = adjustedValue - Math.floor(adjustedValue);
  const epsilon = 1e-8; // Allow for rounding errors in fractionalPart

  if (Math.abs(fractionalPart - 0.5) < epsilon) {
    // If fractional part is exactly 0.5, round to the nearest even number
    return (
      (Math.floor(adjustedValue) % 2 === 0
        ? Math.floor(adjustedValue)
        : Math.floor(adjustedValue) + 1) / multiplier
    );
  } else {
    return roundedValue / multiplier;
  }
}

/**
 * Preforms a floating-point-error safe way of summing numeric values
 * To get the raw value, simply call `.toNumber()` on the result
 * @param values An array of numeric values
 *
 * @example
 * sum([1, 2, 3]) => 6
 */
export const sum = (values: Numeric[]) => {
  return values.reduce(
    (accumulator: Decimal, value: Numeric) =>
      add(accumulator, toDecimal(value)),
    new Decimal(0),
  );
};

/**
 * Preforms a floating-point-error safe way of summing numeric values of a property in a list of objects
 * @param items An array of objects
 * @param key Property name on obect of value to sum
 *j
 * @example
 * sumListValue([{itemId: 1, cost: 10.50, price: 15.75 }, {itemId: 1, cost: 20, price: 30 }], "price") => 45.75
 */
export function sumListValue<T>(items: T[], key: keyof T): number {
  return items
    .reduce((accumulator: Decimal, item: T) => {
      const numberValue = Number(item[key]);
      if (isNaN(numberValue)) {
        throw new Error(`Value of ${key.toString()} is not a number.`);
      }
      return add(accumulator, toDecimal(numberValue));
    }, new Decimal(0))
    .toNumber();
}

/**
 * Preforms a floating-point-error safe way of summing numeric values
 * If all are null, return null, otherwise, sum the non-null values
 * @param values An array of numeric values
 *
 * @example
 * sumNullable([1, 2, 3, null]) => 6
 * sumNullable([null, null]) => null
 */
export function sumNullable(values: (Numeric | null)[]): number | null {
  const nonNulls = values.filter((v) => v !== null);
  if (nonNulls.length > 0) {
    return nonNulls
      .reduce(
        (accumulator: Decimal, value: Numeric | null) =>
          add(accumulator, toDecimal(value)),
        new Decimal(0),
      )
      .toNumber();
  }
  return null;
}

/**
 * Preforms a floating-point-error safe way of subtracting numeric values
 * If all are null, return null, otherwise, subtract the non-null values in order
 * @param values An array of numeric values
 *
 * @example
 * subtractNullable(10, [null, 2, 3]) => 5
 * subtractNullable(null, [null, 2, 3]) => -5
 * subtractNullable(null, [null, null, null]) => null
 */
export function subtractNullable(
  startingValue: Numeric | null,
  values: (Numeric | null)[],
): number | null {
  const nonNulls = values.filter((v) => v !== null);
  if (startingValue !== null || nonNulls.length > 0) {
    return nonNulls
      .reduce(
        (accumulator: Decimal, value: Numeric | null) =>
          subtract(accumulator, toDecimal(value)),
        toDecimal(startingValue),
      )
      .toNumber();
  }
  return null;
}
